import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import { ROLES } from '@/lib/constants/roles';
import type { Role } from '@/lib/constants/roles';
import { AuthUtils } from '@/lib/utils/authUtils';
import type { RoleNames } from '@/lib/utils/authUtils';

/**
 * Composable per la gestione dell'autenticazione e autorizzazione
 *
 * Fornisce accesso reattivo alle informazioni dell'utente e metodi per verificare
 * i ruoli e le autorizzazioni. Progettato per l'uso con script setup e Composition API.
 *
 * @example
 * // In un componente script setup
 * <script setup lang="ts">
 * import { useAuth } from '@/composables/useAuth'
 * import { ROLES } from '@/lib/constants/roles'
 *
 * const { userInfo, isInRole, isInRoles, isPureManager } = useAuth()
 *
 * // Computed reattivi
 * const isAdmin = computed(() => isInRole(ROLES.ADMIN))
 * const canManageUsers = computed(() => isInRoles([ROLES.ADMIN, ROLES.SYS_ADMIN]))
 * const showManagerPanel = computed(() => isPureManager())
 * </script>
 *
 * @example
 * // In template con renderizzazione condizionale
 * <template>
 *   <div>
 *     <el-button v-if="isInRole(ROLES.SYS_ADMIN)" @click="deleteUser">
 *       Elimina Utente
 *     </el-button>
 *
 *     <div v-if="isPureManager()" class="manager-panel">
 *       Pannello Manager (senza privilegi admin)
 *     </div>
 *   </div>
 * </template>
 *
 * @returns Oggetto con userInfo reattivo e metodi di verifica ruoli
 *
 * @category Authentication
 * @since 1.0.0
 */
export function useAuth() {
	// Accesso al store Pinia
	const authStore = useAuthStore();
	const { userInfo } = storeToRefs(authStore);

	/**
	 * Verifica se l'utente corrente possiede un ruolo specifico
	 *
	 * @param roleName - Nome del ruolo da verificare (usare ROLES.* per type safety)
	 * @returns true se l'utente ha il ruolo specificato, false altrimenti
	 *
	 * @example
	 * // Verifica diretta
	 * if (isInRole(ROLES.SYS_ADMIN)) {
	 *   console.log('Utente è SysAdmin');
	 * }
	 *
	 * @example
	 * // In computed reattivo
	 * const canEditUsers = computed(() => isInRole(ROLES.ADMIN))
	 */
	const isInRole = (roleName: Role | string): boolean => {
		return AuthUtils.isInRole(userInfo.value, roleName);
	};

	/**
	 * Verifica se l'utente corrente possiede almeno uno dei ruoli specificati
	 *
	 * @param roleNames - Array di nomi di ruoli da verificare
	 * @returns true se l'utente ha almeno uno dei ruoli specificati, false altrimenti
	 *
	 * @example
	 * // Verifica multipli ruoli
	 * if (isInRoles([ROLES.ADMIN, ROLES.SYS_ADMIN])) {
	 *   console.log('Utente ha privilegi amministrativi');
	 * }
	 */
	const isInRoles = (roleNames: RoleNames): boolean => {
		return AuthUtils.isInRoles(userInfo.value, roleNames);
	};

	/**
	 * Verifica se l'utente è un "pure manager" (solo manager, senza privilegi admin)
	 *
	 * @returns true se l'utente è solo Manager (senza ruoli admin), false altrimenti
	 *
	 * @example
	 * // Logica di business differenziata
	 * if (isPureManager()) {
	 *   // Carica dati team limitati
	 * } else if (isInRole(ROLES.ADMIN)) {
	 *   // Carica tutti i dati aziendali
	 * }
	 */
	const isPureManager = (): boolean => {
		return AuthUtils.isPureManager(userInfo.value);
	};

	// Computed reattivi per casi d'uso comuni
	const isAuthenticated = computed(() => !!userInfo.value);
	const isAdmin = computed(() => isInRole(ROLES.ADMIN));
	const isSysAdmin = computed(() => isInRole(ROLES.SYS_ADMIN));
	const isManager = computed(() => isInRole(ROLES.MANAGER));
	const hasAdminPrivileges = computed(() => isInRoles([ROLES.ADMIN, ROLES.SYS_ADMIN]));

	return {
		// Dati reattivi
		userInfo,

		// Metodi di verifica
		isInRole,
		isInRoles,
		isPureManager,

		// Computed comuni
		isAuthenticated,
		isAdmin,
		isSysAdmin,
		isManager,
		hasAdminPrivileges,
	};
}
